import type {
	ActiveGame,
	AnalysisResult,
	ApiResponse,
	GamesPage,
	Leaderboard,
	LeaderboardLiveGame,
	LeaderboardRange,
	PlMatch,
	Scoreboard,
	ScoutResult,
	Suggestion,
	SummonerProfile,
	TierList,
	ProTierList
} from "./types";
import { turnstileHeaders } from "./utils/turnstile";

const API_BASE = "/api";

/** A failed API call. `retryAfterSeconds` is set when Riot (or our per-IP limit) asked the caller to wait. */
export class ApiError extends Error {
	constructor(
		message: string,
		readonly status: number,
		readonly retryAfterSeconds?: number,
		readonly cooldownRemaining?: number
	) {
		super(message);
	}
}

/**
 * One API call. The server only asks for a Turnstile token on requests that would spend Riot calls, so we try
 * without one first and retry once with a fresh token when it answers `needsTurnstile`.
 */
async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
	let response = await fetch(`${API_BASE}${path}`, init);
	let body: ApiResponse<T> = await response.json().catch(() => ({ success: false, error: "Bad response" }));

	if (response.status === 403 && body.needsTurnstile) {
		const headers = { ...(init.headers as Record<string, string> | undefined), ...(await turnstileHeaders()) };
		response = await fetch(`${API_BASE}${path}`, { ...init, headers });
		body = await response.json().catch(() => ({ success: false, error: "Bad response" }));
	}

	if (!response.ok || !body.success) {
		throw new ApiError(body.error || "Request failed", response.status, body.retryAfterSeconds, body.cooldownRemaining);
	}
	return body.data as T;
}

const qs = (params: Record<string, string | number | undefined>) => {
	const p = new URLSearchParams();
	for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== "") p.set(k, String(v));
	const s = p.toString();
	return s ? `?${s}` : "";
};

const summoner = (region: string, nameTag: string) => `/summoners/${encodeURIComponent(region)}/${encodeURIComponent(nameTag)}`;

/** Profile: ranks, recent games, champion stats. May trigger a refresh from Riot when the data is stale. */
export function fetchSummoner(region: string, nameTag: string, queue?: string): Promise<SummonerProfile> {
	return call(`${summoner(region, nameTag)}${qs({ queue })}`);
}

/** "Load more": games older than `before` (epoch ms), from our database or from Riot's history. */
export function fetchMoreGames(region: string, nameTag: string, before: number, queue?: string): Promise<GamesPage> {
	return call(`${summoner(region, nameTag)}/matches${qs({ before, queue })}`);
}

/** Manual "Update". 429 with `cooldownRemaining` while the 2-minute cooldown runs. */
export function refreshSummoner(region: string, nameTag: string, queue?: string): Promise<SummonerProfile> {
	return call(`${summoner(region, nameTag)}/refresh${qs({ queue })}`, { method: "POST" });
}

export function fetchScoreboard(matchId: string): Promise<Scoreboard> {
	return call(`/matches/${encodeURIComponent(matchId)}`);
}

interface LiveResponse {
	inGame: boolean;
	game?: Omit<ActiveGame, "participants"> & {
		participants: Array<Omit<ActiveGame["participants"][number], "isTracked" | "puuid"> & { isSearched: boolean }>;
	};
}

/** Live game for the summoner, or null when they are not in one. */
export async function fetchLiveGame(region: string, nameTag: string): Promise<ActiveGame | null> {
	const data = await call<LiveResponse>(`${summoner(region, nameTag)}/live`);
	if (!data.inGame || !data.game) return null;
	return { ...data.game, participants: data.game.participants.map((p, i) => ({ ...p, puuid: `p${i}`, isTracked: p.isSearched })) };
}

/** Autocomplete over summoners already seen. Never throws: a failed suggestion must not block typing. */
export async function fetchSuggestions(query: string, region: string): Promise<Suggestion[]> {
	try {
		return await call<Suggestion[]>(`/search/suggest${qs({ q: query, region })}`);
	} catch {
		return [];
	}
}

/**
 * Team scout, step 1 for one player: stores their recent solo queue, Flex, Clash and tournament games.
 * `refreshing` stays true while games are still arriving in the background; asking again is free.
 */
export function syncScoutPlayer(region: string, nameTag: string): Promise<{ summoner: { name: string; tagLine: string }; refreshing: boolean }> {
	return call(`${summoner(region, nameTag)}/scout`, { method: "POST" });
}

const MAX_ROUNDS = 4; // waits for background games before giving up on them

/**
 * Syncs a roster one player per request (each stores their recent ranked, Clash and tournament games), then waits a
 * few paced rounds for older games arriving in the background. Returns the Riot IDs as Riot spells them (the typed
 * spelling may differ from the stored one). An error names the player it happened on, except a 429 (retry as a whole).
 */
export async function syncRoster(region: string, roster: { name: string; tag: string }[], onProgress: (text: string) => void): Promise<string[]> {
	const one = async (r: { name: string; tag: string }) => {
		try {
			return await syncScoutPlayer(region, `${r.name}-${r.tag}`);
		} catch (e) {
			throw e instanceof ApiError && e.status !== 429 ? new ApiError(`${r.name}#${r.tag}: ${e.message}`, e.status, e.retryAfterSeconds) : e;
		}
	};
	let pending: typeof roster = [];
	const riotIds: string[] = [];
	for (const [i, r] of roster.entries()) {
		onProgress(`Loading ${r.name}#${r.tag} (${i + 1} of ${roster.length})`);
		const p = await one(r);
		riotIds.push(`${p.summoner.name}#${p.summoner.tagLine}`);
		if (p.refreshing) pending.push(r);
	}
	// asking again costs no Riot calls; paced to stay under the per-IP limit
	for (let round = 0; pending.length && round < MAX_ROUNDS; round++) {
		onProgress(`Fetching older games for ${pending.length} ${pending.length === 1 ? "player" : "players"}`);
		await new Promise(done => setTimeout(done, 3000 * pending.length));
		const still: typeof roster = [];
		for (const r of pending) if ((await one(r)).refreshing) still.push(r);
		pending = still;
	}
	return riotIds;
}

/** Champion tiers per lane from high-elo solo queue. */
export function fetchTierList(): Promise<TierList> {
	return call("/tierlist");
}

/** Champion picks per lane in a pro tournament (by id; newest without `event`), with the tournament list. */
export function fetchProTierList(event?: string): Promise<ProTierList> {
	return call(`/tierlist/pro${event ? `?event=${encodeURIComponent(event)}` : ""}`);
}

/** A saved scouting result by its share id (the /scout/<id> link). */
export function fetchSharedScout(id: string): Promise<ScoutResult> {
	return call(`/scout/${encodeURIComponent(id)}`);
}

/** Scouting report for the opponent's roster of Riot IDs ("Name#TAG"), against our own roster when given. Reads stored games only. */
/** roles[i] is the position of riotIds[i] (TOP | JUNGLE | MIDDLE | BOTTOM | UTILITY); the same for ownRoles. */
export function scoutTeam(region: string, riotIds: string[], roles: string[], ownRiotIds: string[] = [], ownRoles: string[] = []): Promise<ScoutResult> {
	return call("/scout", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ region, riotIds, roles, ownRiotIds, ownRoles }) });
}

/** Tournament games of a roster since `since` (epoch ms): every game with both line-ups and bans, plus the totals. Reads stored games only. */
/** One Prime League match page, read by the worker (or from D1). id = the number in its link. */
export function fetchPlMatch(id: number): Promise<PlMatch> {
	return call(`/pl/matches/${id}`);
}

/** matchIds: only these games (the linked Prime League matches) */
export function analyseTeam(region: string, riotIds: string[], since: number, matchIds?: string[]): Promise<AnalysisResult> {
	return call("/analysis", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ region, riotIds, since, matchIds }) });
}

// ---- leaderboards: the slug is the view link, the edit key (kept in localStorage) allows changes ----

const board = (slug: string) => `/leaderboards/${encodeURIComponent(slug)}`;
const json = (method: string, body: unknown, editKey?: string): RequestInit => ({
	method,
	headers: { "Content-Type": "application/json", ...(editKey ? { "x-edit-key": editKey } : {}) },
	body: body === undefined ? undefined : JSON.stringify(body)
});

/** The edit key is returned this once. */
export function createLeaderboard(name: string): Promise<{ slug: string; editKey: string }> {
	return call("/leaderboards", json("POST", { name }));
}

// Every call that answers with the board takes the range the page shows, so an edit does not switch it.
export function fetchLeaderboard(slug: string, range: LeaderboardRange, editKey?: string): Promise<Leaderboard> {
	return call(`${board(slug)}${qs({ range })}`, editKey ? { headers: { "x-edit-key": editKey } } : {});
}

export function fetchLeaderboardLive(slug: string): Promise<{ inGame: LeaderboardLiveGame[] }> {
	return call(`${board(slug)}/live`);
}

export function addLeaderboardMember(slug: string, range: LeaderboardRange, editKey: string, region: string, riotId: string): Promise<Leaderboard> {
	return call(`${board(slug)}/members${qs({ range })}`, json("POST", { region, riotId }, editKey));
}

export function removeLeaderboardMember(slug: string, range: LeaderboardRange, editKey: string, id: number): Promise<Leaderboard> {
	return call(`${board(slug)}/members/${id}${qs({ range })}`, json("DELETE", undefined, editKey));
}

export function renameLeaderboard(slug: string, range: LeaderboardRange, editKey: string, name: string): Promise<Leaderboard> {
	return call(`${board(slug)}${qs({ range })}`, json("PATCH", { name }, editKey));
}

export function deleteLeaderboard(slug: string, editKey: string): Promise<{ deleted: boolean }> {
	return call(board(slug), json("DELETE", undefined, editKey));
}
